import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  ListDashes, MagnifyingGlass, DownloadSimple, Eye, ArrowClockwise, User,
  ChartBar, SquaresFour,
} from "@phosphor-icons/react";
import DateRangePicker, { rangeToParams } from "@/components/DateRangePicker";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, Legend,
} from "recharts";

const PAGE_SIZE = 500;
const EMPTY_FILTERS = {
  q: "", customer: "", reseller: "", ai_engine: "", campaign: "",
  transfer_result: "", disposition: "", amd_result: "",
};

const CHART_COLORS = ["#0d9488", "#0284c7", "#059669", "#e11d48", "#d97706", "#64748b", "#0891b2", "#b45309"];
const tooltipStyle = {
  background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))",
  borderRadius: 10, fontSize: 12, color: "hsl(var(--popover-foreground))",
};
const axis = { stroke: "hsl(var(--muted-foreground))", fontSize: 11 };

const buildFilterParams = (f, range) => {
  const p = {};
  Object.entries(f).forEach(([k, v]) => { if (v) p[k] = v; });
  return { ...p, ...rangeToParams(range) };
};
const nn = (v) => (v === null || v === undefined || v === "" ? "—" : v);
const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");
const dur = (s) => (s === null || s === undefined ? "—" : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`);
const humanize = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

/** True when Collect Customer Info captured at least one non-empty value. */
const hasCollectedData = (r) => {
  const info = r?.customer_info;
  if (!info || typeof info !== "object") return false;
  return Object.values(info).some((item) => {
    const v = item && typeof item === "object" ? item.value : item;
    return v != null && String(v).trim() !== "";
  });
};

const DISP_FAIL = new Set([
  "no_response", "transfer_failed", "caller_hangup", "rejected_prefix",
  "no_runnable_flow", "engine_error", "no_audio", "failed", "error",
  "campaign stopped", "campaign not active", "campaign_stopped", "campaign_inactive",
]);
const DISP_LABELS = {
  dead_end: "Incomplete Flow (no next step)",
  call_timeout: "Call Timeout",
  max_call_duration: "Call Timeout",
  "campaign stopped": "Campaign stopped",
  campaign_stopped: "Campaign stopped",
  "campaign not active": "Campaign not active",
  campaign_inactive: "Campaign not active",
};
const dispLabel = (d) => DISP_LABELS[String(d || "").toLowerCase()] || humanize(d);
const dispClass = (d) => {
  if (!d) return "bg-muted text-muted-foreground";
  const k = String(d).toLowerCase();
  if (k === "transfer_succeeded" || k === "completed") return "bg-emerald-100 text-emerald-700 border border-emerald-200";
  if (DISP_FAIL.has(k)) return "bg-rose-100 text-rose-700 border border-rose-200";
  return "bg-amber-100 text-amber-700 border border-amber-200";
};

const amdClass = (v) => {
  const k = String(v || "").toLowerCase();
  if (k === "human") return "bg-emerald-100 text-emerald-700 border border-emerald-200";
  if (k === "machine") return "bg-rose-100 text-rose-700 border border-rose-200";
  if (k === "notsure") return "bg-amber-100 text-amber-700 border border-amber-200";
  return "bg-muted text-muted-foreground";
};
const amdLabel = (v, action, layer) => {
  // Append the detection layer to voicemail/machine results -> "Voicemail:1" (FreeSWITCH)
  // or "Voicemail:2" (AI engine python). Other results are unchanged.
  const suffix = (layer === 1 || layer === 2) ? `:${layer}` : "";
  if (String(action || "").toLowerCase() === "voicemail_left") return `Voicemail Left${suffix}`;
  const k = String(v || "").toLowerCase();
  const base = { human: "Human", machine: "Voicemail", notsure: "Not sure",
    disabled: "Off", error: "Err" }[k] || null;
  if (!base) return null;
  return k === "machine" ? `${base}${suffix}` : base;
};

/**
 * @param {{ channel?: "direct" | "reseller" | null }} props
 * Admin /cdr → direct customers; Admin /cdr/reseller → reseller customers.
 * Reseller/customer roles ignore channel (tenant scope already applies).
 */
export default function CDR({ channel = null }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isReseller = user?.role === "reseller";
  const isCustomer = user?.role === "customer";
  // Admin default CDR is direct-only; Reseller CDR page passes channel="reseller".
  const effectiveChannel = isAdmin ? (channel === "reseller" ? "reseller" : "direct") : null;
  const isResellerChannel = effectiveChannel === "reseller";
  const pageSize = PAGE_SIZE;
  const rowDisp = (r) => ((isReseller || isAdmin) && r.reseller_disposition) ? r.reseller_disposition : r.disposition;

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [range, setRange] = useState(null);
  const [appliedRange, setAppliedRange] = useState(null);
  const [engines, setEngines] = useState([]);
  const [details, setDetails] = useState(null);
  const [infoModal, setInfoModal] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [stats, setStats] = useState(null);
  const [dispOptions, setDispOptions] = useState([]);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioState, setAudioState] = useState("idle");
  const [greeting, setGreeting] = useState(null);
  const [greetingState, setGreetingState] = useState("idle");
  const [view, setView] = useState("table"); // table | chart

  const channelParams = useMemo(() => {
    if (!effectiveChannel) return {};
    return { channel: effectiveChannel };
  }, [effectiveChannel]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page, page_size: pageSize,
        ...buildFilterParams(applied, appliedRange),
        ...channelParams,
      };
      const { data } = await api.get("/cdr", { params });
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, [page, pageSize, applied, appliedRange, channelParams]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!details?.id) { setAudioUrl(null); setAudioState("idle"); return; }
    let cancelled = false; let objUrl = null;
    setAudioState("loading"); setAudioUrl(null);
    api.get(`/cdr/${details.id}/audio`, { responseType: "blob" })
      .then((res) => {
        if (cancelled) return;
        objUrl = window.URL.createObjectURL(res.data);
        setAudioUrl(objUrl); setAudioState("ready");
      })
      .catch((e) => {
        if (cancelled) return;
        setAudioState(e.response?.status === 404 ? "none" : "error");
      });
    return () => { cancelled = true; if (objUrl) window.URL.revokeObjectURL(objUrl); };
  }, [details?.id]);

  useEffect(() => {
    if (!details?.id) { setGreeting(null); setGreetingState("idle"); return; }
    let cancelled = false;
    setGreetingState("loading"); setGreeting(null);
    api.get(`/cdr/${details.id}/greeting`)
      .then((res) => {
        if (cancelled) return;
        const t = (res.data?.text || "").trim();
        setGreeting(t); setGreetingState(t ? "ready" : "empty");
      })
      .catch((e) => {
        if (cancelled) return;
        setGreetingState(e.response?.status === 404 ? "none" : "error");
      });
    return () => { cancelled = true; };
  }, [details?.id]);

  useEffect(() => {
    api.get("/cdr/stats", { params: { ...buildFilterParams(applied, appliedRange), ...channelParams } })
      .then(({ data }) => setStats(data)).catch(() => setStats(null));
  }, [applied, appliedRange, channelParams]);

  useEffect(() => {
    api.get("/ai-engines/overview").then(({ data }) => setEngines(data.engines || [])).catch(() => {});
    api.get("/cdr/stats", { params: channelParams })
      .then(({ data }) => setDispOptions(Object.keys(data.by_disposition || {}))).catch(() => {});
  }, [channelParams]);

  // Reset paging/filters when switching Direct ↔ Reseller CDR.
  useEffect(() => {
    setFilters(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
    setRange(null);
    setAppliedRange(null);
    setPage(1);
    setView("table");
  }, [effectiveChannel]);

  const engineOptions = engines.length ? engines.map((e) => ({ id: e.engine_id, name: e.name }))
    : [...new Map(rows.filter((r) => r.ai_engine_id)
        .map((r) => [r.ai_engine_id, { id: r.ai_engine_id, name: r.ai_engine_name || r.ai_engine_id }])).values()];

  // Clone filters/range so a new reference is set on every click — this guarantees
  // the list AND the /cdr/stats (chart/KPI) effects refetch even when nothing changed,
  // so Apply works every time instead of only once.
  const apply = () => { setPage(1); setApplied({ ...filters }); setAppliedRange(range ? { ...range } : range); };
  const reset = () => {
    setFilters(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
    setRange(null);
    setAppliedRange(null);
    setPage(1);
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = { ...buildFilterParams(applied, appliedRange), ...channelParams };
      const res = await api.get("/cdr/export", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = isResellerChannel ? "reseller-cdrs.csv" : "cdrs.csv";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setExporting(false); }
  };

  const pages = Math.max(1, Math.ceil(total / pageSize));
  const summary = stats?.summary || {};
  const amdCounts = useMemo(() => {
    const la = stats?.by_amd_layer || {};
    const sum = (o) => Object.values(o || {}).reduce((a, b) => a + (b || 0), 0);
    const l1 = sum(la["1"]), l2 = sum(la["2"]);
    return { l1, l2, total: l1 + l2 };
  }, [stats]);
  const chartData = useMemo(() => {
    const entries = Object.entries(stats?.by_disposition || {})
      .sort((a, b) => b[1] - a[1]);
    const top = entries.slice(0, 8);
    const rest = entries.slice(8);
    const other = rest.reduce((n, [, v]) => n + v, 0);
    const rowsChart = top.map(([k, v]) => ({ name: dispLabel(k), key: k, count: v }));
    if (other > 0) rowsChart.push({ name: "Other", key: "other", count: other });
    return rowsChart;
  }, [stats]);
  const amdChartData = useMemo(() => {
    const la = stats?.by_amd_layer || {};
    const l1 = la["1"] || {}, l2 = la["2"] || {};
    const cats = [
      { key: "machine", name: "Voicemail" },
      { key: "human", name: "Human" },
      { key: "notsure", name: "Not sure" },
    ];
    return cats
      .map((c) => ({ name: c.name, "Layer 1": l1[c.key] || 0, "Layer 2": l2[c.key] || 0 }))
      .filter((r) => r["Layer 1"] > 0 || r["Layer 2"] > 0);
  }, [stats]);

  const title = isResellerChannel ? "Reseller CDR" : "Call Detail Records";
  const subtitle = isResellerChannel
    ? "CDRs for customers under resellers — outcomes, AI agent duration, and AI charges."
    : isAdmin && effectiveChannel === "direct"
      ? "Direct-customer calls only (Lead ID, phone, campaign, disposition, AI charges)."
      : isCustomer
        ? "Per-call outcomes for your campaigns (phone, campaign, disposition, collected data)."
        : "Per-call VICIdial metadata and outcome for AI-engine calls.";

  const kpiCards = [
    { key: "total", label: "Total Calls", value: stats?.total ?? 0, testid: "cdr-stat-total" },
    { key: "xfer_ok", label: "Transfer OK", value: summary.transfer_succeeded ?? 0, testid: "cdr-stat-xfer-ok" },
    { key: "xfer_fail", label: "Transfer Failed", value: summary.transfer_failed ?? 0, testid: "cdr-stat-xfer-fail" },
    { key: "hangup", label: "Caller Hangup", value: summary.caller_hangup ?? 0, testid: "cdr-stat-hangup" },
    {
      key: "amd",
      label: "AMD Detected",
      value: amdCounts.total,
      testid: "cdr-stat-amd",
      sub: `L1: ${amdCounts.l1} \u00b7 L2: ${amdCounts.l2}`,
    },
    {
      key: "ai_charge",
      label: "AI Charges",
      value: money(summary.ai_charge_minor ?? 0),
      testid: "cdr-stat-ai-charges",
      money: true,
    },
  ];

  return (
    <div>
      <PageHeader title={title} subtitle={subtitle}>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-border p-0.5 bg-muted/40" data-testid="cdr-view-toggle">
            <Button
              size="sm" variant={view === "table" ? "secondary" : "ghost"}
              className="rounded-md h-8 px-2.5"
              onClick={() => setView("table")}
              data-testid="cdr-view-table"
            >
              <SquaresFour className="w-4 h-4 mr-1" /> Table
            </Button>
            <Button
              size="sm" variant={view === "chart" ? "secondary" : "ghost"}
              className="rounded-md h-8 px-2.5"
              onClick={() => setView("chart")}
              data-testid="cdr-view-chart"
            >
              <ChartBar className="w-4 h-4 mr-1" /> Chart
            </Button>
          </div>
          <Button variant="outline" onClick={exportCsv} disabled={exporting} data-testid="cdr-export-btn" className="rounded-lg">
            <DownloadSimple className="w-4 h-4 mr-1.5" weight="bold" /> {exporting ? "Exporting…" : "Export CSV"}
          </Button>
        </div>
      </PageHeader>

      {/* Compact KPI cards — fixed set, not one-per-disposition */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-5" data-testid="cdr-stats-cards">
          {kpiCards.map((c) => (
            <div key={c.key} className="rounded-xl border border-border bg-card card-shadow p-4" data-testid={c.testid}>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">{c.label}</p>
              <p className={`font-semibold tabular-nums mt-1 ${c.money ? "text-xl" : "text-2xl"}`}>{c.value}</p>
              {c.sub ? <p className="text-[11px] text-muted-foreground mt-1 tabular-nums" data-testid={`${c.testid}-sub`}>{c.sub}</p> : null}
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="rounded-xl border border-border bg-card card-shadow p-4 mb-5 grid gap-3 md:grid-cols-6" data-testid="cdr-filters">
        <div className="space-y-1 md:col-span-2">
          <Label className="text-xs">{isCustomer ? "Search (phone)" : "Search (phone / lead / unique id)"}</Label>
          <Input data-testid="cdr-search" value={filters.q} placeholder="e.g. 1005551234"
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && apply()} />
        </div>
        {!isCustomer && (
          <div className="space-y-1">
            <Label className="text-xs">AI Engine</Label>
            <select data-testid="cdr-filter-engine" className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
              value={filters.ai_engine} onChange={(e) => setFilters({ ...filters, ai_engine: e.target.value })}>
              <option value="">All</option>
              {engineOptions.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
        )}
        <div className="space-y-1">
          <Label className="text-xs">Campaign</Label>
          <Input data-testid="cdr-filter-campaign" value={filters.campaign} placeholder="id / prefix"
            onChange={(e) => setFilters({ ...filters, campaign: e.target.value })} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Transfer result</Label>
          <select data-testid="cdr-filter-transfer" className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
            value={filters.transfer_result} onChange={(e) => setFilters({ ...filters, transfer_result: e.target.value })}>
            <option value="">All</option>
            <option value="transfer_succeeded">Transfer succeeded</option>
            <option value="transfer_failed">Transfer failed</option>
            <option value="not_attempted">Not attempted</option>
          </select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Disposition</Label>
          <select data-testid="cdr-filter-disposition" className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
            value={filters.disposition} onChange={(e) => setFilters({ ...filters, disposition: e.target.value })}>
            <option value="">All</option>
            {dispOptions.map((d) => <option key={d} value={d}>{dispLabel(d)}</option>)}
          </select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">AMD</Label>
          <select data-testid="cdr-filter-amd" className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
            value={filters.amd_result} onChange={(e) => setFilters({ ...filters, amd_result: e.target.value })}>
            <option value="">All</option>
            <option value="human">Human</option>
            <option value="machine">Voicemail</option>
            <option value="notsure">Not sure</option>
            <option value="disabled">Off</option>
            <option value="error">Error</option>
          </select>
        </div>
        <div className="space-y-1 md:col-span-2">
          <Label className="text-xs">Date range</Label>
          <DateRangePicker value={range} onChange={setRange} testid="cdr-daterange" />
        </div>
        {!isCustomer && (
          <div className="space-y-1">
            <Label className="text-xs">Customer</Label>
            <Input data-testid="cdr-filter-customer" value={filters.customer} placeholder="Customer name"
              onChange={(e) => setFilters({ ...filters, customer: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && apply()} />
          </div>
        )}
        {isResellerChannel && (
          <div className="space-y-1">
            <Label className="text-xs">Reseller</Label>
            <Input data-testid="cdr-filter-reseller" value={filters.reseller} placeholder="Reseller name"
              onChange={(e) => setFilters({ ...filters, reseller: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && apply()} />
          </div>
        )}
        <div className="md:col-span-6 flex gap-2">
          <Button size="sm" className="rounded-lg" onClick={apply} data-testid="cdr-apply-btn">
            <MagnifyingGlass className="w-4 h-4 mr-1.5" /> Apply
          </Button>
          <Button size="sm" variant="ghost" className="rounded-lg" onClick={reset} data-testid="cdr-reset-btn">
            <ArrowClockwise className="w-4 h-4 mr-1.5" /> Reset
          </Button>
          <span className="ml-auto self-center text-xs text-muted-foreground" data-testid="cdr-total">{total} records</span>
        </div>
      </div>

      {view === "chart" ? (
        <>
        <div className="rounded-xl border border-border bg-card card-shadow p-5 mb-5" data-testid="cdr-chart">
          <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
            <div>
              <h3 className="text-sm font-semibold">Disposition mix</h3>
              <p className="text-xs text-muted-foreground mt-0.5">Top outcomes for the current filters</p>
            </div>
            <div className="text-right" data-testid="cdr-chart-ai-charges">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">AI Charges</p>
              <p className="text-xl font-semibold tabular-nums">{money(summary.ai_charge_minor ?? 0)}</p>
            </div>
          </div>
          {chartData.length === 0 ? (
            <EmptyState testid="cdr-chart-empty" icon={ChartBar} title="No chart data"
              description="Apply filters or wait for calls to complete." />
          ) : (
            <div className="h-[360px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                  <XAxis type="number" tick={axis} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="name" width={140} tick={axis} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v) => [v, "Calls"]} />
                  <Bar dataKey="count" name="Calls" radius={[0, 4, 4, 0]}>
                    {chartData.map((entry, i) => (
                      <Cell key={entry.key} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
        <div className="rounded-xl border border-border bg-card card-shadow p-5 mb-5" data-testid="cdr-amd-chart">
          <div className="mb-4">
            <h3 className="text-sm font-semibold">AMD detection by layer</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Layer 1 (FreeSWITCH) vs Layer 2 (AI engine) for the current filters</p>
          </div>
          {amdChartData.length === 0 ? (
            <EmptyState testid="cdr-amd-chart-empty" icon={ChartBar} title="No AMD data"
              description="AMD detections appear here once calls with AMD enabled complete." />
          ) : (
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={amdChartData} margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis type="category" dataKey="name" tick={axis} axisLine={false} tickLine={false} />
                  <YAxis type="number" allowDecimals={false} tick={axis} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="Layer 1" name="Layer 1 \u00b7 FreeSWITCH" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Layer 2" name="Layer 2 \u00b7 AI Engine" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
        </>
      ) : loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : rows.length === 0 ? (
        <EmptyState testid="cdr-empty" icon={ListDashes} title="No call records"
          description={isResellerChannel
            ? "Reseller-customer CDRs appear here after their AI-engine calls complete."
            : isCustomer
              ? "CDRs appear here after calls complete. Each record shows the phone, campaign, outcome, and whether customer info was collected."
              : "CDRs appear here after AI-engine calls complete."} />
      ) : (
        <div className="rounded-xl border border-border bg-card card-shadow overflow-x-auto" data-testid="cdr-table">
          <Table>
            <TableHeader><TableRow>
              {!isCustomer && <TableHead>Lead ID</TableHead>}
              <TableHead>Phone</TableHead>
              {!isCustomer && <TableHead>CALLERID</TableHead>}
              <TableHead>Client</TableHead>
              {isResellerChannel && <TableHead>Reseller</TableHead>}
              {!isCustomer && <TableHead>Customer</TableHead>}
              <TableHead>Campaign</TableHead>
              {!isCustomer && <TableHead>Unique ID</TableHead>}
              <TableHead>Start</TableHead>
              <TableHead className="text-right">Dur</TableHead>
              <TableHead className="text-right">AI Agent dur.</TableHead>
              <TableHead className="text-right">AI charge</TableHead>
              {!isCustomer && <TableHead>AI Engine</TableHead>}
              <TableHead>Transfer</TableHead>
              <TableHead>Disposition</TableHead>
              <TableHead>AMD</TableHead>
              {isCustomer && <TableHead className="text-center">Customer info</TableHead>}
              <TableHead className="text-right">Details</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} className="hover:bg-muted/40" data-testid={`cdr-row-${r.id}`}>
                  {!isCustomer && (
                    <TableCell className="font-mono text-xs" data-testid={`cdr-lead-${r.id}`}>{nn(r.lead_id)}</TableCell>
                  )}
                  <TableCell className="font-mono text-sm">{nn(r.phone_number)}</TableCell>
                  {!isCustomer && <TableCell className="text-sm">{nn(r.callerid)}</TableCell>}
                  <TableCell className="font-mono text-xs">{nn(r.client_id)}</TableCell>
                  {isResellerChannel && (
                    <TableCell className="text-xs" data-testid={`cdr-reseller-${r.id}`}>{nn(r.reseller_name)}</TableCell>
                  )}
                  {!isCustomer && (
                    <TableCell className="text-xs" data-testid={`cdr-customer-${r.id}`}>{nn(r.customer_name)}</TableCell>
                  )}
                  <TableCell className="text-xs">{nn(r.campaign_id)}{r.campaign_prefix ? ` / ${r.campaign_prefix}` : ""}</TableCell>
                  {!isCustomer && (
                    <TableCell className="font-mono text-xs truncate max-w-[130px]">{nn(r.unique_id)}</TableCell>
                  )}
                  <TableCell className="text-xs text-muted-foreground">{fmt(r.start_time)}</TableCell>
                  <TableCell className="text-right tabular-nums text-xs">{dur(r.duration_sec)}</TableCell>
                  <TableCell className="text-right tabular-nums text-xs" data-testid={`cdr-agent-dur-${r.id}`}>
                    {r.ai_agent_duration_sec > 0 ? dur(r.ai_agent_duration_sec) : "—"}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-xs" data-testid={`cdr-ai-charge-${r.id}`}>
                    {r.ai_charge_minor > 0 ? money(r.ai_charge_minor) : "—"}
                  </TableCell>
                  {!isCustomer && (
                    <TableCell className="text-xs">{nn(r.ai_engine_name || r.ai_engine_id)}</TableCell>
                  )}
                  <TableCell className="text-xs">{nn(r.transfer_result)}</TableCell>
                  <TableCell className="text-xs">
                    {r.disposition
                      ? <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${dispClass(rowDisp(r))}`} data-testid={`cdr-disp-${r.id}`}>{dispLabel(rowDisp(r))}</span>
                      : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-xs">
                    {amdLabel(r.amd_result, r.amd_action, r.amd_layer)
                      ? <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${amdClass(r.amd_result)}`} data-testid={`cdr-amd-${r.id}`} title={r.amd_cause || ""}>{amdLabel(r.amd_result, r.amd_action, r.amd_layer)}</span>
                      : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  {isCustomer && (
                    <TableCell className="text-center" data-testid={`cdr-data-${r.id}`}>
                      {hasCollectedData(r) ? (
                        <button
                          type="button"
                          className="inline-flex items-center justify-center rounded-full p-1.5 text-teal-700 hover:bg-teal-50"
                          title="View collected customer info"
                          onClick={() => setInfoModal(r)}
                          data-testid={`cdr-data-icon-${r.id}`}
                        >
                          <User className="w-4 h-4" weight="fill" />
                        </button>
                      ) : null}
                    </TableCell>
                  )}
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" className="rounded-lg px-2" onClick={() => setDetails(r)} data-testid={`cdr-details-${r.id}`}><Eye className="w-4 h-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {view === "table" && total > pageSize && (
        <div className="flex items-center justify-between mt-4 text-sm" data-testid="cdr-pagination">
          <span className="text-muted-foreground">Page {page} of {pages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="rounded-lg" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="cdr-prev">Prev</Button>
            <Button variant="outline" size="sm" className="rounded-lg" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="cdr-next">Next</Button>
          </div>
        </div>
      )}

      <Dialog open={!!infoModal} onOpenChange={(v) => !v && setInfoModal(null)}>
        <DialogContent data-testid="cdr-customer-info-dialog">
          <DialogHeader>
            <DialogTitle>Customer info</DialogTitle>
            <DialogDescription>
              Collected during call {nn(infoModal?.phone_number)}
              {infoModal?.start_time ? ` · ${fmt(infoModal.start_time)}` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            {Object.entries(infoModal?.customer_info || {}).map(([key, item]) => {
              const row = item && typeof item === "object" ? item : { value: item };
              return (
                <div key={key} data-testid={`cdr-info-modal-${key}`}>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">{row.label || key}</p>
                  <p className="text-sm break-all">{row.value || "—"}</p>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!details} onOpenChange={(v) => !v && setDetails(null)}>
        <DialogContent data-testid="cdr-details-dialog">
          <DialogHeader><DialogTitle>Call Detail</DialogTitle>
            <DialogDescription>
              {isCustomer
                ? `Phone ${nn(details?.phone_number)}`
                : `Unique ID ${nn(details?.unique_id)}`}
            </DialogDescription></DialogHeader>
          <div className="py-2 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            {[
              ...(!isCustomer ? [["Lead ID", details?.lead_id]] : []),
              ["Phone (Caller-Id)", details?.phone_number],
              ...(!isCustomer ? [["CALLERID (value)", details?.callerid]] : []),
              ["Client ID", details?.client_id],
              ...(isResellerChannel ? [["Reseller", details?.reseller_name]] : []),
              ...(!isCustomer ? [["Customer", details?.customer_name]] : []),
              ["Campaign ID", details?.campaign_id], ["Campaign prefix", details?.campaign_prefix],
              ...(!isCustomer ? [["Unique ID", details?.unique_id], ["FS UUID", details?.fs_uuid]] : []),
              ...(!isCustomer ? [
                ["AI Engine", details?.ai_engine_name || details?.ai_engine_id],
                ["AI Engine IP", details?.ai_engine_ip],
              ] : []),
              ...(!isCustomer ? [
                [isAdmin ? "OpenSIPS IP" : "Telephony IP", details?.opensips_ip],
                [isAdmin ? "FreeSWITCH IP" : "Media IP", details?.freeswitch_ip],
              ] : []),
              ...(isAdmin ? [["Capacity at selection", details?.capacity_at_selection != null ? `${details.capacity_at_selection}${details.max_capacity != null ? ` / ${details.max_capacity}` : ""}` : null]] : []),
              ["Start", details?.start_time && fmt(details.start_time)],
              ["End", details?.end_time && fmt(details.end_time)],
              ["Duration", details?.duration_sec != null ? dur(details.duration_sec) : null],
              ["AI Agent dur.", details?.ai_agent_duration_sec > 0 ? dur(details.ai_agent_duration_sec) : null],
              ["AI charge", details?.ai_charge_minor > 0 ? money(details.ai_charge_minor) : null],
              ...(details?.rate_snapshot?.ai_model_price_minor != null
                ? [["AI Model per min", money(details.rate_snapshot.ai_model_price_minor)]]
                : []),
              ...((isAdmin || isReseller) && details?.rate_snapshot?.ai_billing_status
                ? [["AI billing status", details.rate_snapshot.ai_billing_status]]
                : []),
              ["Transfer result", details?.transfer_result], ["Disposition", details?.disposition ? dispLabel(details.disposition) : null],
              ...((isAdmin || isReseller) && details?.reseller_disposition ? [["Reseller disposition", dispLabel(details.reseller_disposition)]] : []),
              ["Hangup cause", details?.hangup_cause],
              ["AMD result", amdLabel(details?.amd_result, details?.amd_action)],
              ["AMD cause", details?.amd_cause],
              ["AMD decided (ms)", details?.amd_decided_ms],
              ["AMD Detection", details?.amd_layer ? (details.amd_layer === 1 ? "Layer 1 \u00b7 FreeSWITCH" : "Layer 2 \u00b7 AI Engine") : "\u2014"],
              ["AMD Layer 1 (FreeSWITCH)", details?.amd_l1_result ? `${details.amd_l1_result}${details?.amd_l1_cause ? " / " + details.amd_l1_cause : ""}` : "\u2014"],
              ...(!isCustomer ? [["Transcript", details?.transcript_reference]] : []),
            ].map(([k, v]) => (
              <div key={k} data-testid={`cdr-detail-${k.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{k}</p>
                <p className="font-mono text-xs break-all">{nn(v)}</p>
              </div>
            ))}
          </div>
          {!isCustomer && details?.customer_info && Object.keys(details.customer_info).length > 0 && (
            <div className="mt-1 border-t border-border pt-3" data-testid="cdr-customer-info">
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1.5">Customer info</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                {Object.entries(details.customer_info).map(([key, item]) => {
                  const row = item && typeof item === "object" ? item : { value: item };
                  return (
                    <div key={key} data-testid={`cdr-customer-info-${key}`}>
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">{row.label || key}</p>
                      <p className="text-sm break-all">{row.value || "—"}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <div className="mt-1 border-t border-border pt-3" data-testid="cdr-greeting">
            <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1.5">Initial Customer Greeting (speech)</p>
            {greetingState === "loading" && <p className="text-xs text-muted-foreground">Transcribing greeting…</p>}
            {greetingState === "ready" && <p className="text-sm break-words" data-testid="cdr-greeting-text">&ldquo;{greeting}&rdquo;</p>}
            {greetingState === "empty" && <p className="text-xs text-muted-foreground" data-testid="cdr-greeting-empty">No speech detected in the greeting.</p>}
            {greetingState === "none" && <p className="text-xs text-muted-foreground" data-testid="cdr-greeting-none">No greeting sample available for this call.</p>}
            {greetingState === "error" && <p className="text-xs text-rose-600" data-testid="cdr-greeting-error">Couldn\'t load greeting.</p>}
          </div>
          <div className="mt-1 border-t border-border pt-3" data-testid="cdr-recording">
            <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1.5">Call Recording (AMD sample)</p>
            {audioState === "loading" && <p className="text-xs text-muted-foreground">Loading recording…</p>}
            {audioState === "ready" && audioUrl && (
              <audio controls preload="none" src={audioUrl} className="w-full" data-testid="cdr-audio-player" />
            )}
            {audioState === "none" && <p className="text-xs text-muted-foreground" data-testid="cdr-audio-none">No recording available for this call.</p>}
            {audioState === "error" && <p className="text-xs text-rose-600" data-testid="cdr-audio-error">Couldn't load recording (engine unreachable).</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
